import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import type { CandleData, OrderData } from '../types/chartData'
import type { BacktestChartHandle } from '../components/BacktestChart'
import { DatafeedSocket } from '../adapters/datafeedSocket'
import { fetchCandleRange, fetchCandlesBefore } from '../adapters/candleSocket'
import { fetchBacktestEvents } from '../adapters/eventFetcher'
import { binarySearchClosest } from '../../utils/chartHelpers'
import {
  getCandleCountForTimeframe,
  getWindowBarsForTimeframe,
  getMaxBarsForTimeframe,
  getTimeframeSeconds,
  formatDateLabel,
  formatTimeLabel,
} from '../../utils/chartUtils'

/**
 * Market gaps (weekends, holidays) mean N bars span more than N * timeframe of
 * wall-clock time. Every time-range request is widened by this factor so a
 * window still comes back roughly full. The datafeed applies the same slack
 * server-side for count-based requests (_WINDOW_SAFETY).
 */
const GAP_SLACK = 2

export interface UseGraphCandleWindowOptions {
  symbol: string
  timeframe: string
  rangeStartTs: number
  rangeEndTs: number
  btid?: string
  programmaticRangeRef?: React.MutableRefObject<{ from: number; to: number } | null>
  rangeChangeSuppressedRef?: React.MutableRefObject<boolean>
  initialMaxAvailableTs?: number | null
  displayBoundaryTs?: number | null
  initialCenterTs?: number | null
  onSliderJump?: (targetTs: number) => void
}

export default function useGraphCandleWindow({
  symbol,
  timeframe,
  rangeStartTs,
  rangeEndTs,
  btid,
  programmaticRangeRef,
  rangeChangeSuppressedRef,
  initialMaxAvailableTs = null,
  displayBoundaryTs = null,
  initialCenterTs = null,
  onSliderJump,
}: UseGraphCandleWindowOptions) {
  const rangeTotal = rangeEndTs - rangeStartTs

  const [candles, setCandles] = useState<CandleData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [sliderPosition, setSliderPosition] = useState(0)
  const [maxAvailableTs, setMaxAvailableTs] = useState<number | null>(
    initialMaxAvailableTs !== null && initialMaxAvailableTs < rangeEndTs ? initialMaxAvailableTs : null,
  )
  const [orders, setOrders] = useState<OrderData[]>([])

  const chartRef = useRef<BacktestChartHandle>(null)
  // Window bounds as unix seconds — the datafeed's own units. (These were Dates
  // when candles arrived as one HTTP file per calendar day.)
  const windowStartTsRef = useRef(rangeStartTs)
  const windowEndTsRef = useRef(rangeStartTs)
  const isFetchingLeftRef = useRef(false)
  const isFetchingRightRef = useRef(false)
  /** `${extStartTs}:${hardEnd}` of a tail request that came back with nothing new. */
  const rightDryProbeRef = useRef<string | null>(null)
  const candlesRef = useRef<CandleData[]>([])
  const isDraggingRef = useRef(false)
  const sliderBarRef = useRef<HTMLDivElement>(null)
  const suppressRangeChangeRef = useRef(false)
  const sliderPositionRef = useRef(0)
  const rangeShiftRef = useRef(0)
  const resetViewRef = useRef(false)
  const maxAvailableTsRef = useRef<number | null>(null)
  const maxSliderFractionRef = useRef(1)
  const centerDateTsRef = useRef<number | null>(null)
  const dragCleanupRef = useRef<(() => void) | null>(null)
  const visibleRangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const externalSuppressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewportAnchorTsRef = useRef<number | null>(initialCenterTs)
  const extendCooldownUntilRef = useRef(0)
  const previousEffectiveCountRef = useRef(0)
  // Invalidates in-flight extendWindow calls when timeframe/symbol changes,
  // OR when a fresh loadWindow re-centers the chart (any earlier extension
  // becomes stale relative to the new center).
  const windowEpochRef = useRef(0)
  // Invalidates in-flight loadWindow calls when a newer loadWindow starts.
  // Without this, two concurrent loadWindows could land out of order and
  // leave windowStart/windowEnd/orders/candles all desynced.
  const loadEpochRef = useRef(0)

  // One datafeed socket per chart: the server's message loop is strictly serial
  // per connection, so a shared socket would queue unrelated charts behind each
  // other. Created lazily because the constructor throws when DATAFEED_WS is
  // unset, and that must surface as a load error rather than a render crash.
  const socketRef = useRef<DatafeedSocket | null>(null)
  const getSocket = useCallback((): DatafeedSocket => {
    if (!socketRef.current) socketRef.current = new DatafeedSocket()
    return socketRef.current
  }, [])

  useEffect(() => {
    return () => {
      socketRef.current?.dispose()
      socketRef.current = null
    }
  }, [])

  useLayoutEffect(() => { candlesRef.current = candles }, [candles])
  useLayoutEffect(() => { sliderPositionRef.current = sliderPosition }, [sliderPosition])
  useLayoutEffect(() => { maxAvailableTsRef.current = maxAvailableTs }, [maxAvailableTs])

  const isInProgress = maxAvailableTs !== null && maxAvailableTs < rangeEndTs
  const progressPercent = maxAvailableTs !== null
    ? Math.min(100, ((maxAvailableTs - rangeStartTs) / rangeTotal) * 100)
    : 100
  const maxSliderFraction = isInProgress ? Math.max(0, (maxAvailableTs - rangeStartTs) / rangeTotal) : 1
  useLayoutEffect(() => { maxSliderFractionRef.current = maxSliderFraction }, [maxSliderFraction])

  const effectiveCandleCount = useMemo(() => {
    if (candles.length === 0) return 0

    let count = candles.length

    if (maxAvailableTs !== null) {
      let lo = 0, hi = candles.length
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (candles[mid].time <= maxAvailableTs) lo = mid + 1
        else hi = mid
      }
      count = lo
    }

    if (displayBoundaryTs !== null) {
      let lo = 0, hi = candles.length
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (candles[mid].time <= displayBoundaryTs) lo = mid + 1
        else hi = mid
      }
      count = Math.min(count, lo)
    }

    return count
  }, [candles, maxAvailableTs, displayBoundaryTs])

  const setExternalSuppression = useCallback((value: boolean) => {
    if (rangeChangeSuppressedRef) {
      rangeChangeSuppressedRef.current = value
    }
  }, [rangeChangeSuppressedRef])

  const releaseRangeSuppression = useCallback(() => {
    if (visibleRangeTimerRef.current) {
      clearTimeout(visibleRangeTimerRef.current)
      visibleRangeTimerRef.current = null
    }
    if (externalSuppressionTimerRef.current) {
      clearTimeout(externalSuppressionTimerRef.current)
      externalSuppressionTimerRef.current = null
    }
    suppressRangeChangeRef.current = false
    if (programmaticRangeRef) {
      programmaticRangeRef.current = null
    }
    setExternalSuppression(false)
  }, [programmaticRangeRef, setExternalSuppression])

  const applyVisibleRange = useCallback((range: { from: number; to: number }) => {
    if (!chartRef.current) return
    suppressRangeChangeRef.current = true
    extendCooldownUntilRef.current = Date.now() + 150
    setExternalSuppression(true)
    if (programmaticRangeRef) {
      programmaticRangeRef.current = range
    }
    chartRef.current.setVisibleLogicalRange(range)
    if (visibleRangeTimerRef.current) {
      clearTimeout(visibleRangeTimerRef.current)
    }
    visibleRangeTimerRef.current = setTimeout(() => {
      visibleRangeTimerRef.current = null
      suppressRangeChangeRef.current = false
    }, 120)
    if (externalSuppressionTimerRef.current) {
      clearTimeout(externalSuppressionTimerRef.current)
    }
    externalSuppressionTimerRef.current = setTimeout(() => {
      externalSuppressionTimerRef.current = null
      if (programmaticRangeRef) {
        programmaticRangeRef.current = null
      }
      setExternalSuppression(false)
    }, 160)
  }, [programmaticRangeRef, setExternalSuppression])

  const buildVisibleRange = useCallback((anchorTs: number | null) => {
    if (effectiveCandleCount === 0) return null

    const visibleCount = getCandleCountForTimeframe(timeframe, effectiveCandleCount)
    let from = 0
    let to = Math.min(visibleCount - 1, effectiveCandleCount - 1)

    if (anchorTs !== null) {
      const centerIdx = Math.min(binarySearchClosest(candles, anchorTs), effectiveCandleCount - 1)
      const half = Math.floor(visibleCount / 2)
      from = Math.max(0, centerIdx - half)
      to = from + visibleCount - 1
      if (to >= effectiveCandleCount) {
        to = effectiveCandleCount - 1
        from = Math.max(0, to - visibleCount + 1)
      }
    }

    return { from, to }
  }, [candles, timeframe, effectiveCandleCount])

  const clampVisibleRange = useCallback((range: { from: number; to: number }) => {
    if (effectiveCandleCount === 0) return null

    const maxIndex = effectiveCandleCount - 1
    const span = Math.max(1, Math.round(range.to - range.from + 1))
    let from = Math.max(0, Math.min(maxIndex, Math.round(range.from)))
    let to = Math.max(0, Math.min(maxIndex, Math.round(range.to)))

    if (to < from) {
      to = from
    }

    const desiredSpan = Math.min(span, effectiveCandleCount)
    if (to - from + 1 < desiredSpan) {
      if (from === 0) {
        to = Math.min(maxIndex, from + desiredSpan - 1)
      } else {
        from = Math.max(0, to - desiredSpan + 1)
      }
    }

    return { from, to }
  }, [effectiveCandleCount])

  // ---- Visible range adjustment after candle data changes ----
  // useLayoutEffect prevents 1-frame visual jumps by running before browser paint
  useLayoutEffect(() => {
    const previousEffectiveCount = previousEffectiveCountRef.current
    previousEffectiveCountRef.current = effectiveCandleCount

    if (resetViewRef.current) {
      resetViewRef.current = false
      rangeShiftRef.current = 0
      const savedCenterTs = centerDateTsRef.current
      centerDateTsRef.current = null

      if (effectiveCandleCount === 0 || !chartRef.current) {
        releaseRangeSuppression()
        return
      }

      const anchorTs = savedCenterTs ?? viewportAnchorTsRef.current
      if (anchorTs !== null) {
        viewportAnchorTsRef.current = anchorTs
      }
      const nextRange = buildVisibleRange(anchorTs)
      if (nextRange) {
        applyVisibleRange(nextRange)
      } else {
        releaseRangeSuppression()
      }
      return
    }

    if (rangeShiftRef.current !== 0) {
      if (effectiveCandleCount === 0 || !chartRef.current) return
      const shift = rangeShiftRef.current
      rangeShiftRef.current = 0
      const range = chartRef.current.getVisibleLogicalRange()
      if (range) {
        const shiftedRange = clampVisibleRange({ from: range.from + shift, to: range.to + shift })
        if (shiftedRange) {
          applyVisibleRange(shiftedRange)
        }
      }
      return
    }

    if (effectiveCandleCount === 0 || !chartRef.current) return

    const range = chartRef.current.getVisibleLogicalRange()
    if (!range) {
      const initialRange = buildVisibleRange(viewportAnchorTsRef.current)
      if (initialRange) {
        applyVisibleRange(initialRange)
      }
      return
    }

    const clampedRange = clampVisibleRange(range)
    if (!clampedRange) return

    const rangeWasClamped = clampedRange.from !== Math.round(range.from) || clampedRange.to !== Math.round(range.to)
    const countShrank = previousEffectiveCount > 0 && effectiveCandleCount < previousEffectiveCount

    if (countShrank && rangeWasClamped) {
      const anchoredRange = buildVisibleRange(viewportAnchorTsRef.current)
      if (anchoredRange) {
        applyVisibleRange(anchoredRange)
      }
      return
    }

    if (rangeWasClamped) {
      applyVisibleRange(clampedRange)
    }
  }, [
    candles,
    timeframe,
    effectiveCandleCount,
    displayBoundaryTs,
    maxAvailableTs,
    applyVisibleRange,
    buildVisibleRange,
    clampVisibleRange,
    releaseRangeSuppression,
  ])

  // ---- Load a window around a center date ----
  const loadWindow = useCallback(async (centerDate: Date) => {
    // Mark this as the newest load AND invalidate any in-flight extendWindow
    // calls (their windowStart/windowEnd will no longer match what we commit).
    // Older loadWindow promises detect a newer epoch and bail.
    const loadEpoch = ++loadEpochRef.current
    windowEpochRef.current++
    // A new window may sit anywhere, so a previous "tail is empty" answer says nothing.
    rightDryProbeRef.current = null

    const centerTs = Math.floor(centerDate.getTime() / 1000)
    centerDateTsRef.current = centerTs
    viewportAnchorTsRef.current = centerTs
    suppressRangeChangeRef.current = true
    setExternalSuppression(true)
    setIsLoading(true)

    // Half a window of bars either side of the cursor, widened for market gaps.
    const halfSpanSec = (getWindowBarsForTimeframe(timeframe) / 2) * getTimeframeSeconds(timeframe) * GAP_SLACK
    const windowStartTs = Math.max(rangeStartTs, centerTs - halfSpanSec)
    const windowEndTs = Math.min(rangeEndTs, centerTs + halfSpanSec)

    try {
      const candlePromise = fetchCandleRange(getSocket(), symbol, timeframe, windowStartTs, windowEndTs)
      const eventsPromise = btid
        ? fetchBacktestEvents(btid, windowStartTs, windowEndTs)
        : Promise.resolve([] as OrderData[])

      const [page, eventOrders] = await Promise.all([candlePromise, eventsPromise])
      if (loadEpochRef.current !== loadEpoch) return

      // The datafeed answers from the bar CONTAINING `from`, so a window pinned to the
      // range start comes back with one bar that opens before the run does. Clip it: the
      // same rule extendWindow('left') already applies, and every consumer downstream
      // (replay cursor, index math, the drawings reveal) assumes no bar precedes
      // rangeStartTs.
      const data = page.length > 0 && page[0].time < rangeStartTs
        ? page.filter(c => c.time >= rangeStartTs)
        : page

      windowStartTsRef.current = data.length > 0 ? data[0].time : windowStartTs
      windowEndTsRef.current = data.length > 0 ? data[data.length - 1].time : windowEndTs
      resetViewRef.current = true
      rangeShiftRef.current = 0
      setLoadError(null)
      setCandles(data)
      setOrders(eventOrders)

      const clampedTs = Math.max(rangeStartTs, Math.min(centerTs, rangeEndTs))
      setSliderPosition(Math.max(0, Math.min(maxSliderFractionRef.current, (clampedTs - rangeStartTs) / rangeTotal)))
    } catch (err) {
      if (loadEpochRef.current !== loadEpoch) return
      // The old HTTP fetcher swallowed every failure and returned [], so a dead
      // datafeed looked like a symbol with no data. Surface it instead.
      setLoadError(err instanceof Error ? err.message : 'Failed to load candles')
      resetViewRef.current = true
      setCandles([])
      setOrders([])
    } finally {
      if (loadEpochRef.current === loadEpoch) {
        setIsLoading(false)
      }
      // If we discarded this load (newer one in flight), leave isLoading
      // alone — the newer load owns it. Suppression flags will be cleared
      // by the layoutEffect after the winning load's setCandles paints, or
      // by releaseRangeSuppression on unmount.
    }
  }, [symbol, timeframe, rangeStartTs, rangeEndTs, rangeTotal, btid, getSocket, setExternalSuppression])

  const initialCenterRef = useRef(initialCenterTs)

  // ---- Initial load & timeframe/symbol change ----
  // Re-center on the current viewport anchor (where the user is actually
  // looking) instead of snapping the chart back to rangeStart. loadWindow
  // bumps windowEpochRef internally, which invalidates any in-flight extends.
  useEffect(() => {
    const initialCenter = initialCenterRef.current
    initialCenterRef.current = null
    const center = initialCenter ?? viewportAnchorTsRef.current ?? rangeStartTs
    loadWindow(new Date(center * 1000))
  }, [timeframe, symbol, loadWindow, rangeStartTs])

  // ---- Extend the window in one direction (with pruning) ----
  const extendWindow = useCallback(async (direction: 'left' | 'right') => {
    const fetchingRef = direction === 'left' ? isFetchingLeftRef : isFetchingRightRef
    if (fetchingRef.current) return
    fetchingRef.current = true
    const epoch = windowEpochRef.current // checked after each await to discard stale fetches
    const windowBars = getWindowBarsForTimeframe(timeframe)
    const maxBars = getMaxBarsForTimeframe(timeframe)
    const pageBars = Math.max(200, Math.floor(windowBars / 4))
    const tfSec = getTimeframeSeconds(timeframe)

    try {
      const current = candlesRef.current
      if (current.length === 0) return

      if (direction === 'right') {
        const hardEnd = maxAvailableTsRef.current !== null
          ? Math.min(maxAvailableTsRef.current, rangeEndTs)
          : rangeEndTs
        const lastTs = current[current.length - 1].time
        const extStartTs = lastTs + 1
        // Bound the range by bars, not by "everything left in the backtest" —
        // an unbounded tail request would blow the socket's 20s timeout.
        const extEndTs = Math.min(hardEnd, extStartTs + pageBars * tfSec * GAP_SLACK)
        if (extEndTs <= extStartTs) return
        // Already asked for exactly this and it had nothing new. Without this the pan
        // re-requests the tail on every range event, because a right-pan at the end of
        // the data reports the same visible range over and over. Keyed on hardEnd too, so
        // a still-running backtest re-probes as its ceiling advances.
        const probe = `${extStartTs}:${hardEnd}`
        if (rightDryProbeRef.current === probe) return

        const candlePromise = fetchCandleRange(getSocket(), symbol, timeframe, extStartTs, extEndTs)
        const eventsPromise = btid
          ? fetchBacktestEvents(btid, extStartTs, extEndTs)
          : Promise.resolve([] as OrderData[])

        const [page, freshEvents] = await Promise.all([candlePromise, eventsPromise])
        if (windowEpochRef.current !== epoch) return

        // The datafeed floors `from` to the bar CONTAINING it, so a request starting at
        // lastTs + 1 comes back including the bar at lastTs — and once the window reaches
        // the end of the data that is the only bar it returns. Appending the page verbatim
        // therefore re-added the same candle on every pan, without limit: the series grew
        // with copies of its own final bar, so the chart never ran out of bars to scroll
        // through and the same candle repeated across the screen.
        const fresh = page.filter(c => c.time > lastTs)
        windowEndTsRef.current = fresh.length > 0 ? fresh[fresh.length - 1].time : extEndTs
        if (fresh.length === 0) {
          rightDryProbeRef.current = probe
          return
        }
        rightDryProbeRef.current = null

        if (freshEvents.length > 0) {
          setOrders(prev => {
            if (windowEpochRef.current !== epoch) return prev
            const ids = new Set(prev.map(o => o.orderId))
            const newOnes = freshEvents.filter(o => !ids.has(o.orderId))
            return newOnes.length > 0 ? [...prev, ...newOnes] : prev
          })
        }

        if (fresh.length > 0) {
          // Compute next candles + pruning boundary synchronously using the
          // current candles ref. The epoch check above already protects us
          // from concurrent loadWindow stomping our work; using a synchronous
          // computation here (instead of a setState callback) lets us derive
          // the order-prune boundary in the same tick.
          const prevCandles = candlesRef.current
          const combined = [...prevCandles, ...fresh]
          let nextCandles = combined
          let pruneBoundaryTs: number | null = null
          if (combined.length > maxBars) {
            const excess = combined.length - maxBars
            rangeShiftRef.current = -excess
            nextCandles = combined.slice(excess)
            if (nextCandles.length > 0) {
              windowStartTsRef.current = nextCandles[0].time
              pruneBoundaryTs = nextCandles[0].time
            }
          }
          setCandles(nextCandles)
          if (pruneBoundaryTs !== null) {
            const boundary = pruneBoundaryTs
            setOrders(prev => {
              const filtered = prev.filter(o => o.time >= boundary)
              return filtered.length === prev.length ? prev : filtered
            })
          }
        }
      } else {
        const extEndTs = current[0].time - 1
        if (extEndTs <= rangeStartTs) return

        // Count-based paging going backwards: the bar count is what we want to
        // bound, and gaps make the equivalent time span unpredictable.
        const page = await fetchCandlesBefore(getSocket(), symbol, timeframe, extEndTs, pageBars)
        if (windowEpochRef.current !== epoch) return
        // Never page past the backtest's own range start, and never re-add a bar the
        // window already holds — the datafeed snaps a boundary request to the bar
        // containing it, so an overlap is the fetcher's normal behaviour, not an anomaly.
        const firstTs = current[0].time
        const fresh = page.filter(c => c.time >= rangeStartTs && c.time < firstTs)
        if (fresh.length === 0) return

        const freshEvents = btid
          ? await fetchBacktestEvents(btid, fresh[0].time, extEndTs)
          : []
        if (windowEpochRef.current !== epoch) return
        windowStartTsRef.current = fresh[0].time

        if (freshEvents.length > 0) {
          setOrders(prev => {
            if (windowEpochRef.current !== epoch) return prev
            const ids = new Set(prev.map(o => o.orderId))
            const newOnes = freshEvents.filter(o => !ids.has(o.orderId))
            return newOnes.length > 0 ? [...newOnes, ...prev] : prev
          })
        }

        // Prepending shifts all existing candle indices to the right by
        // fresh.length. The visible logical range is index-based, so we
        // must mirror the shift to keep the same time region on screen.
        const prevCandles = candlesRef.current
        const combined = [...fresh, ...prevCandles]
        let nextCandles = combined
        let pruneBoundaryTs: number | null = null
        if (combined.length > maxBars) {
          nextCandles = combined.slice(0, maxBars)
        }
        if (nextCandles.length > 0) {
          windowEndTsRef.current = nextCandles[nextCandles.length - 1].time
          if (nextCandles.length < combined.length) {
            pruneBoundaryTs = nextCandles[nextCandles.length - 1].time
          }
        }
        rangeShiftRef.current = fresh.length
        setCandles(nextCandles)
        if (pruneBoundaryTs !== null) {
          const boundary = pruneBoundaryTs
          setOrders(prev => {
            const filtered = prev.filter(o => o.time <= boundary)
            return filtered.length === prev.length ? prev : filtered
          })
        }
      }
    } catch {
      // An extension failure is non-fatal: the loaded window still renders, and
      // the next pan or replay tick retries. Don't clobber loadError.
    } finally {
      fetchingRef.current = false
    }
  }, [symbol, timeframe, rangeStartTs, rangeEndTs, btid, getSocket])

  // ---- Chart visible range change (scroll detection) ----
  const handleVisibleRangeChange = useCallback((range: { from: number; to: number }) => {
    const now = Date.now()
    const isSuppressed = suppressRangeChangeRef.current
    const isCoolingDown = now < extendCooldownUntilRef.current
    if (isSuppressed || isDraggingRef.current || isCoolingDown) return
    const cur = candlesRef.current
    if (cur.length === 0) return

    const dispCount = chartRef.current?.getCandleCount() ?? cur.length
    if (dispCount <= 0) return
    const centerIdx = Math.floor((range.from + range.to) / 2)
    const clamped = Math.max(0, Math.min(dispCount - 1, centerIdx))
    const ts = cur[clamped]?.time ?? cur[cur.length - 1].time
    viewportAnchorTsRef.current = ts
    setSliderPosition(Math.max(0, Math.min(maxSliderFractionRef.current, (ts - rangeStartTs) / rangeTotal)))

    const threshold = Math.max(20, (range.to - range.from) * 0.3)
    const isClippedByDisplayBoundary = displayBoundaryTs !== null && dispCount < cur.length
    const shouldExtendLeft = range.from < threshold
    const shouldExtendRight = !isClippedByDisplayBoundary && range.to > dispCount - threshold
    if (shouldExtendLeft) extendWindow('left')
    if (shouldExtendRight) extendWindow('right')
  }, [extendWindow, rangeStartTs, rangeTotal, displayBoundaryTs])

  // ---- Slider interaction ----
  const onSliderJumpRef = useRef(onSliderJump)
  useLayoutEffect(() => { onSliderJumpRef.current = onSliderJump }, [onSliderJump])

  const jumpToSliderPosition = useCallback((fraction: number) => {
    const clampedFrac = Math.min(fraction, maxSliderFractionRef.current)
    const targetTs = rangeStartTs + clampedFrac * rangeTotal
    onSliderJumpRef.current?.(targetTs)
    // Center on the exact target: windows are bar-centred now, so the old
    // snap-to-local-midnight would land the view a day off.
    loadWindow(new Date(targetTs * 1000))
  }, [loadWindow, rangeStartTs, rangeTotal])

  const handleSliderMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()

    const bar = sliderBarRef.current
    if (!bar) return

    isDraggingRef.current = true

    const computeFraction = (clientX: number) => {
      const rect = bar.getBoundingClientRect()
      return Math.max(0, Math.min(maxSliderFractionRef.current, (clientX - rect.left) / rect.width))
    }

    const fraction = computeFraction(e.clientX)
    setSliderPosition(fraction)

    const onMouseMove = (ev: MouseEvent) => {
      setSliderPosition(computeFraction(ev.clientX))
    }
    const onMouseUp = (ev: MouseEvent) => {
      isDraggingRef.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      dragCleanupRef.current = null
      jumpToSliderPosition(computeFraction(ev.clientX))
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    dragCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [jumpToSliderPosition])

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.()
      releaseRangeSuppression()
    }
  }, [releaseRangeSuppression])

  // ---- Backtest progress API ----
  const updateBacktestProgress = useCallback((maxDateTime: number) => {
    setMaxAvailableTs(maxDateTime >= rangeEndTs ? null : maxDateTime)
  }, [rangeEndTs])

  // ---- Derived display values ----
  const dateRangeLabel = useMemo(() => {
    if (candles.length === 0) return ''
    const first = candles[0]
    const last = candles[candles.length - 1]
    return `${formatDateLabel(first.time)} ${formatTimeLabel(first.time)} — ${formatDateLabel(last.time)} ${formatTimeLabel(last.time)}`
  }, [candles])

  const windowIndicator = useMemo(() => {
    if (candles.length === 0) return { left: 0, width: 0 }
    const firstTs = candles[0].time
    const lastTs = candles[candles.length - 1].time
    const left = (firstTs - rangeStartTs) / rangeTotal
    const width = (lastTs - firstTs) / rangeTotal
    return { left: Math.max(0, left), width: Math.min(1 - Math.max(0, left), Math.max(0.002, width)) }
  }, [candles, rangeStartTs, rangeTotal])

  const sliderDateLabel = useMemo(() => {
    const ts = rangeStartTs + sliderPosition * rangeTotal
    return formatDateLabel(ts)
  }, [sliderPosition, rangeStartTs, rangeTotal])

  return {
    candles,
    isLoading,
    loadError,
    orders,
    effectiveCandleCount,

    chartRef,
    handleVisibleRangeChange,

    maxAvailableTs,
    isInProgress,
    progressPercent,
    maxSliderFraction,
    updateBacktestProgress,

    sliderPosition,
    sliderBarRef,
    handleSliderMouseDown,

    windowIndicator,
    sliderDateLabel,
    dateRangeLabel,

    loadWindow,
    extendWindow,
    applyVisibleRange,
    // Exported so a caller can centre the viewport on a moment without going through
    // loadWindow: the clamp at `effectiveCandleCount - 1` means the range it builds never runs
    // past the newest REVEALED bar, which is what makes it safe to point at a past trade.
    buildVisibleRange,
    sliderPositionRef,
    maxAvailableTsRef,
  }
}
